import React, { useState } from 'react';
import { apiService } from '../services/apiService';

const ThemeFeedback = ({ featureType, contentId, themeName, aiModel, qualityScore, onSuccess, initialRating = 0, initialThumbs = null, onFeedbackChange }) => {
  const [rating, setRating] = useState(initialRating);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [thumbs, setThumbs] = useState(initialThumbs);
  const [hoveredThumbs, setHoveredThumbs] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Logging utility
  const LOG_PREFIX = `[FEEDBACK] 📊`;
  const log = {
    info: (msg, data) => console.log(`${LOG_PREFIX} ℹ️  ${msg}`, data || ''),
    success: (msg, data) => console.log(`${LOG_PREFIX} ✅ ${msg}`, data || ''),
    error: (msg, error) => console.error(`${LOG_PREFIX} ❌ ${msg}`, error || ''),
  };

  const handleRatingClick = async (selectedRating) => {
    if (submitting) return;
    
    log.info('Rating submission started', { 
      rating: selectedRating, 
      featureType, 
      contentId: contentId || 'unsaved',
      aiModel 
    });
    
    setSubmitting(true);
    try {
      await apiService.feedback.submitRating({
        feature_type: featureType,
        content_id: contentId,
        rating: selectedRating,
        theme_name: themeName,
        ai_model: aiModel,
        quality_score: qualityScore
      });
      
      setRating(selectedRating);
      log.success('Rating submitted successfully', { rating: selectedRating });
      onFeedbackChange?.(selectedRating, thumbs);
      onSuccess?.('Rating submitted successfully!');
    } catch (error) {
      log.error('Rating submission failed', error);
      onSuccess?.('Failed to submit rating', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleThumbsClick = async (selectedThumbs) => {
    if (submitting) return;
    
    log.info('Thumbs submission started', { 
      thumbs: selectedThumbs, 
      featureType, 
      contentId: contentId || 'unsaved',
      aiModel 
    });
    
    setSubmitting(true);
    try {
      await apiService.feedback.submitThumbs({
        feature_type: featureType,
        content_id: contentId,
        thumbs: selectedThumbs,
        theme_name: themeName,
        ai_model: aiModel,
        quality_score: qualityScore
      });
      
      setThumbs(selectedThumbs);
      log.success('Thumbs submitted successfully', { thumbs: selectedThumbs });
      onFeedbackChange?.(rating, selectedThumbs);
      onSuccess?.('Feedback submitted successfully!');
    } catch (error) {
      log.error('Thumbs submission failed', error);
      onSuccess?.('Failed to submit feedback', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8 relative overflow-hidden bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500 group">
      {/* Animated background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-100/40 via-pink-50/30 to-gold-100/40 rounded-3xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
      
      {/* Floating sparkle decorations */}
      <div className="absolute top-3 right-5 text-gold-400 text-lg animate-bounce" style={{ animationDelay: '0.2s' }}>✨</div>
      <div className="absolute top-4 left-6 text-rose-300/50 text-sm animate-pulse">💫</div>
      <div className="absolute bottom-3 right-8 text-pink-300/40 text-sm animate-pulse" style={{ animationDelay: '1s' }}>✨</div>
      <div className="absolute bottom-4 left-5 text-gold-300/40 text-lg animate-bounce" style={{ animationDelay: '0.5s' }}>💖</div>

      {/* Section Header */}
      <div className="text-center mb-6 relative z-10">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-lg mb-3 animate-pulse">
          <span className="text-2xl animate-bounce">💝</span>
        </div>
        <h4 className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent tracking-wide">
          How's this theme looking? ✨
        </h4>
        <p className="text-sm text-gray-500 mt-1 font-light italic">Your feedback helps our AI create even better themes</p>
      </div>

      <div className="flex items-center justify-between gap-8 relative z-10">
        
        {/* Star Rating - Left Side */}
        <div className="flex-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-rose-400 to-pink-400 rounded-xl flex items-center justify-center mr-2 shadow-md">
              <span className="text-sm animate-bounce">💖</span>
            </div>
            <h4 className="text-sm font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent uppercase tracking-wider">Love it? Rate it!</h4>
          </div>
          <div className="flex items-center justify-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleRatingClick(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                disabled={submitting}
                className="transition-all duration-300 transform hover:scale-125 disabled:opacity-50 disabled:cursor-not-allowed p-1"
              >
                <span className={`text-4xl transition-all duration-300 ${
                  star <= (hoveredRating || rating)
                    ? 'text-gold-400 drop-shadow-[0_0_12px_rgba(234,179,8,0.6)] scale-110'
                    : 'text-gray-300/60 hover:text-gold-200'
                }`}>
                  ★
                </span>
              </button>
            ))}
          </div>
          <div className="h-8 flex items-center justify-center mt-2">
            {(rating > 0 || hoveredRating > 0) && (
              <span className={`px-4 py-1.5 rounded-full font-semibold text-xs transition-all duration-300 ${
                hoveredRating > 0 && !rating
                  ? 'bg-gradient-to-r from-rose-50 to-pink-50 text-rose-500 shadow-sm'
                  : 'bg-gradient-to-r from-gold-100 to-yellow-100 text-gold-700 shadow-md shadow-gold-100/50'
              }`}>
                {(() => { const v = hoveredRating || rating; return v === 5 ? '🌟 Wedding dreams come true!' : v === 4 ? '💖 Almost perfect!' : v === 3 ? '👍 Getting there!' : v === 2 ? '🤔 Needs a little magic' : '💭 Not the vibe'; })()}
              </span>
            )}
          </div>
        </div>

        {/* Divider - VowAI gradient style */}
        <div className="w-px h-28 bg-gradient-to-b from-transparent via-rose-300/60 to-transparent relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-gradient-to-r from-rose-400 to-gold-400 rounded-full animate-pulse"></div>
        </div>

        {/* Thumbs Up/Down - Right Side */}
        <div className="flex-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-blue-400 rounded-xl flex items-center justify-center mr-2 shadow-md">
              <span className="text-sm animate-bounce">🎯</span>
            </div>
            <h4 className="text-sm font-serif font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent uppercase tracking-wider">Quick Vibe Check</h4>
          </div>
          <div className="flex items-center justify-center gap-5">
            <button
              onClick={() => handleThumbsClick('up')}
              onMouseEnter={() => setHoveredThumbs('up')}
              onMouseLeave={() => setHoveredThumbs(null)}
              disabled={submitting}
              className={`relative overflow-hidden p-4 rounded-2xl transition-all duration-300 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed border-2 ${
                thumbs === 'up'
                  ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-xl shadow-green-200/50 scale-110 border-emerald-300 ring-4 ring-emerald-200/30'
                  : 'bg-white/80 text-gray-600 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 border-green-200/40 hover:border-emerald-300 hover:shadow-lg hover:shadow-green-100/50'
              }`}
            >
              <span className={`text-3xl ${thumbs === 'up' ? 'animate-bounce' : ''}`}>👍</span>
              {thumbs === 'up' && <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-emerald-400/20 animate-pulse rounded-2xl"></div>}
            </button>
            <button
              onClick={() => handleThumbsClick('down')}
              onMouseEnter={() => setHoveredThumbs('down')}
              onMouseLeave={() => setHoveredThumbs(null)}
              disabled={submitting}
              className={`relative overflow-hidden p-4 rounded-2xl transition-all duration-300 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed border-2 ${
                thumbs === 'down'
                  ? 'bg-gradient-to-r from-rose-400 to-red-500 text-white shadow-xl shadow-rose-200/50 scale-110 border-rose-300 ring-4 ring-rose-200/30'
                  : 'bg-white/80 text-gray-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 border-rose-200/40 hover:border-rose-300 hover:shadow-lg hover:shadow-rose-100/50'
              }`}
            >
              <span className={`text-3xl ${thumbs === 'down' ? 'animate-bounce' : ''}`}>👎</span>
              {thumbs === 'down' && <div className="absolute inset-0 bg-gradient-to-r from-rose-400/20 to-red-400/20 animate-pulse rounded-2xl"></div>}
            </button>
          </div>
          <div className="h-8 flex items-center justify-center mt-2">
            {(thumbs || hoveredThumbs) && (
              <span className={`px-4 py-1.5 rounded-full font-semibold text-xs transition-all duration-300 ${
                (hoveredThumbs || thumbs) === 'up'
                  ? hoveredThumbs && !thumbs ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-emerald-500 shadow-sm' : 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700 shadow-md shadow-emerald-100/50'
                  : hoveredThumbs && !thumbs ? 'bg-gradient-to-r from-rose-50 to-red-50 text-rose-400 shadow-sm' : 'bg-gradient-to-r from-rose-100 to-red-100 text-rose-700 shadow-md shadow-rose-100/50'
              }`}>
                {(hoveredThumbs || thumbs) === 'up' ? '💖 Love at first sight!' : '✨ We\'ll sprinkle more magic!'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeFeedback;
